/* eslint-disable max-lines */
import WithLegalLayout from 'modules/layout/WithLegalLayout';
import { Link } from 'components/Link';

export default function PrivacyPolicy() {
  return (
    <WithLegalLayout title="Privacy Policy">
      <p>
        Sleepedy, Inc. dba Dawn (“<strong>Dawn</strong>,” “<strong>we</strong>,” “<strong>us</strong>,” or “
        <strong>our</strong>”) has prepared this Privacy Policy to explain what personal information we collect, how we
        use and share that information, and your choices concerning our information practices. Through our mobile
        application (the “App”), we offer a range of services focused on helping you improve your sleep habits,
        including through guidance provided by sleep coaches (such services, including{' '}
        <Link href="https://www.dawn.health">
          <a>https://www.dawn.health</a>
        </Link>{' '}
        (the “Site”) and the App, are referred to collectively in this Privacy Policy as the “Service”).
      </p>
      <p>
        This Privacy Policy explains what personal information we collect, how we use and share that information, and
        your choices concerning our information practices. This Privacy Policy is incorporated into and forms part of
        our Terms of Service (https://www.dawn.health/legal/terms-of-service).
      </p>
      <p>
        Before using the Service or submitting any personal information to Dawn, please review this Privacy Policy
        carefully and <a href="#contact-us">contact us</a> if you have any questions. By using the Service, you agree to
        the practices described in this Privacy Policy. If you do not agree to this Privacy Policy, please do not access
        the Site or otherwise use the Service.
      </p>

      <h2>1. PERSONAL INFORMATION WE COLLECT</h2>
      <p>
        <strong>Personal Information You Provide:</strong> We collect the following categories of personal information
        from you when you complete the new user questionnaire, sign up and create a user account, track your sleep
        through the App, and use other functions of the App:
      </p>
      <ul>
        <li>
          <strong>Identification Information:</strong> We collect identification information such as your real name,
          postal address, email address, account name, and other similar identification information.
        </li>
        <li>
          <strong>Financial Information:</strong> Our payment processor(s) will collect the financial information
          necessary to process your payments, such as your payment card number and authentication details. Please note,
          however, that we store only a tokenized version of such information and do not maintain payment card
          information on our servers.
        </li>
        <li>
          <strong>Communication Information:</strong> We may collect information when you{' '}
          <a href="#contact-us">contact us</a> with questions or concerns and when you voluntarily respond to
          questionnaires, surveys or requests for market research seeking your opinion and feedback. Providing
          communication information is optional to you.
        </li>
        <li>
          <strong>Health Information:</strong> We collect your resting heart rate, weight, sleep data, and other health
          data directly from your smart watch or similar device, such as Apple Watch, Fitbit, or Oura Ring.
          Notwithstanding anything else in this Privacy Policy, (a) we use your health information only to provide the
          Service and not for advertising and (b) we will not disclose health information for advertising, marketing, or
          use-based data mining not needed to provide the Service.
        </li>
        <li>
          <strong>Social Media Information:</strong> We have pages on social media sites like Instagram, Facebook,
          Medium, Twitter, and LinkedIn (“Social Media Pages”). When you interact with our Social Media Pages, we will
          collect personal information that you elect to provide to us, such as your contact details. In addition, the
          companies that host our Social Media Pages may provide us with aggregate information and analytics regarding
          the use of our Social Media Pages.
        </li>
      </ul>
      <p>
        <strong>Internet Activity Information:</strong> When you visit, use, and interact with the Service, we may
        receive certain information about your visit, use, or interactions. For example, we may monitor the number of
        people that visit the Service, peak hours of visits, which page(s) are visited, the domains our visitors come
        from (e.g., google.com, yahoo.com, etc.), and which browsers people use to access the Service (e.g., Chrome,
        Firefox, Microsoft Internet Explorer, etc.), broad geographical information, and navigation pattern. In
        particular, the following information is created and automatically logged in our systems:{' '}
      </p>
      <ul>
        <li>
          <strong>Log Information:</strong> Information that your browser automatically sends whenever you visit the
          Site. Log Information includes your Internet Protocol address, browser type and settings, the date and time of
          your request, and how you interacted with the Site.{' '}
        </li>
        <li>
          <strong>Cookies Information:</strong> Please see the “<a href="#cookies">Cookies</a>” section below to learn
          more about how we use cookies.
        </li>
        <li>
          <strong>Device Information:</strong> Includes name of the device, operating system, and browser you are using.
          Information collected may depend on the type of device you use and its settings.{' '}
        </li>
        <li>
          <strong>Usage Information:</strong> We collect information about how you use our Service, such as the types of
          content that you view or engage with, the features you use, the actions you take, and the time, frequency, and
          duration of your activities.
        </li>
      </ul>
      <p id="cookies">
        <strong>Cookies:</strong> We use cookies to operate and administer our Site, gather usage data on our Site, and
        improve your experience on it. A “cookie” is a piece of information sent to your browser by a website you visit.
        Cookies can be stored on your computer for different periods of time. Some cookies expire after a certain amount
        of time, or upon logging out (session cookies), others survive after your browser is closed until a defined
        expiration date set in the cookie (as determined by the third party placing it), and help recognize your
        computer when you open your browser and browse the Internet again (persistent cookies). For more details on
        cookies please visit <a href="https://www.allaboutcookies.org/">All About Cookies</a>.
      </p>
      <p>
        On most web browsers, you will find a “help” section on the toolbar. Please refer to this section for
        information on how to receive a notification when you are receiving a new cookie and how to turn cookies off.{' '}
      </p>
      <p>
        Please note that if you limit the ability of websites to set cookies, you may be unable to access certain parts
        of the Site and you may not be able to benefit from the full functionality of the Site.
      </p>
      <p>
        Advertising networks may use cookies to collect Personal Information. Most advertising networks offer you a way
        to opt out of targeted advertising. If you would like to find out more information, please visit the Network
        Advertising Initiative’s <a href="https://www.networkadvertising.org/">online resources</a> and follow the
        opt-out instructions there.
      </p>
      <p>
        If you access the Site on your mobile device, you may not be able to control tracking technologies through the
        settings.
      </p>
      <p>
        <strong>Analytics:</strong> We use Google Analytics, a web analytics service provided by Google, Inc. (“
        <strong>Google</strong>”). Google Analytics uses cookies to help us analyze how users use the Site and enhance
        your experience when you use the Site. For more information on how Google uses this information, click{' '}
        <a href="http://www.google.com/policies/privacy/partners/">here</a>.
      </p>
      <p>
        <strong>Online Tracking and Do Not Track Signals:</strong> Our Site currently does not respond to “Do Not Track”
        (“DNT”) signals and operates as described in this Privacy Policy whether or not a DNT signal is received. If we
        do respond to DNT signals in the future, we will update this Privacy Policy to describe how we do so.{' '}
      </p>

      <h3>2. HOW WE USE PERSONAL INFORMATION</h3>
      <p>We may use personal information for the following purposes:</p>
      <ul>
        <li>
          To provide you with the Service, including sleep coaching and cognitive behavior therapy for improving
          sleeping habits;
        </li>
        <li>To respond to your inquiries, comments, feedback, or questions;</li>
        <li>
          To send administrative information to you, for example, information regarding the Service and changes to our
          terms, conditions, and policies;
        </li>
        <li>To analyze how you interact with our Service;</li>
        <li>To maintain and improve the Service;</li>
        <li>To develop new products and services;</li>
        <li>
          To prevent fraud, criminal activity, or misuses of our Service, and to ensure the security of our IT systems,
          architecture, and networks; and
        </li>
        <li>
          To comply with legal obligations and legal process and to protect our rights, privacy, safety, or property,
          and/or that of our affiliates, you, or other third parties.
        </li>
      </ul>
      <p>
        <strong>Aggregated Information.</strong> We may aggregate personal information and use the aggregated
        information to analyze the effectiveness of our Service, to improve and add features to our Service, and for
        other similar purposes. In addition, from time to time, we may analyze the general behavior and characteristics
        of users of our Service and share aggregated information like general user statistics with prospective business
        partners. We may collect aggregated information through the Service, through cookies, and through other means
        described in this Privacy Policy.
      </p>
      <p>
        <strong>Marketing.</strong> We may use your personal information to contact you to tell you about products or
        services we believe may be of interest to you. For instance, if you elect to provide your email, we may use that
        information to send you special offers. You may opt out of receiving emails by following the instructions
        contained in each promotional email we send you. In addition, if at any time you do not wish to receive future
        marketing communications, you may <a href="#contact-us">contact us</a>. If you unsubscribe from our marketing
        lists, you will no longer receive marketing communications but we will continue to contact you regarding
        management of your account, other administrative matters, and to respond to your requests.
      </p>

      <h3>3. SHARING AND DISCLOSURE OF PERSONAL INFORMATION</h3>
      <p>
        Dawn does not sell your personal information. In certain circumstances we may share the categories of personal
        information described above without further notice to you, unless required by the law, with the following
        categories of third parties:
      </p>
      <ul>
        <li>
          <strong>Dawn Coaches:</strong> To provide you with the Service, you are matched with at least one sleep coach,
          who will have access to your personal information (including health information) in order to provide you with
          advice and guidance to improve your sleep habits.
        </li>
        <li>
          <strong>Vendors and Service Providers:</strong> To assist us in meeting business operations needs and to
          perform certain services and functions, we may share personal information with vendors and service providers,
          including providers of hosting services, cloud services, and other information technology services providers,
          email communication software and email newsletter services, advertising and marketing services, payment
          processors, customer relationship management and customer support services, and analytics services. Pursuant
          to our instructions, these parties will access, process, or store personal information in the course of
          performing their duties to us. We take commercially reasonable steps to ensure our service providers adhere to
          the <a href="#security">security standards</a> we apply to your personal information.
        </li>
        <li>
          <strong>Business Transfers:</strong> If we are involved in a merger, acquisition, financing due diligence,
          reorganization, bankruptcy, receivership, dissolution, sale of all or a portion of our assets, or transition
          of service to another provider (collectively a “<strong>Transaction</strong>”), your personal information and
          other information may be shared in the diligence process with counterparties and others assisting with the
          Transaction and transferred to a successor or affiliate as part of or following that Transaction along with
          other assets.
        </li>
        <li>
          <strong>Legal Requirements:</strong> If required to do so by law or in the good faith belief that such action
          is necessary to (i) comply with a legal obligation, including to meet national security or law enforcement
          requirements, (ii) protect and defend our rights or property, (iii) prevent fraud, (iv) act in urgent
          circumstances to protect the personal safety of users of the Service, or the public, or (v) protect against
          legal liability.
        </li>
        <li>
          <strong>Affiliates:</strong> We may share personal information with our current and future affiliates, meaning
          an entity that controls, is controlled by, or is under common control with Dawn. Our affiliates may use the
          personal information we share in a manner consistent with this Privacy Policy.
        </li>
        <li>
          <strong>Other Users:</strong> certain actions you take may be visible to other users of the Service. For
          example, your posts will appear in other users’ feeds and your profile information will be accessible to other
          users. You acknowledge that by sharing posts or adding information to your profile you make the information
          shared available to other users and that Dawn cannot control, and shall not be responsible for any use other
          users make of such information.
        </li>
      </ul>

      <h3>4. DATA RETENTION</h3>
      <p>
        We keep personal information for as long as reasonably necessary for the purposes described in this Privacy
        Policy, while we have a business need to do so, or as required by law (e.g. for tax, legal, accounting, or other
        purposes), whichever is longer.
      </p>

      <h3>5. CHILDREN</h3>
      <p>
        Our Service is not directed to children who are under the age of 18. Dawn does not knowingly collect personal
        information from children under the age of 18. If you have reason to believe that a child under the age of 18
        has provided personal information to Dawn through the Service please <a href="#contact-us">contact us</a> and we
        will endeavor to delete that information from our databases.
      </p>

      <h3>6. LINKS TO OTHER WEBSITES</h3>
      <p>
        The Service may contain links to other websites not operated or controlled by Dawn, including social media
        services (“<strong>Third Party Sites</strong>”). The information that you share with Third Party Sites will be
        governed by the specific privacy policies and terms of service of the Third Party Sites and not by this Privacy
        Policy. By providing these links we do not imply that we endorse or have reviewed these sites. Please contact
        the Third Party Sites directly for information on their privacy practices and policies.{' '}
      </p>

      <h3 id="security">7. SECURITY</h3>
      <p>
        You use the Service at your own risk. We implement commercially reasonable technical, administrative, and
        organizational measures to protect personal information both online and offline from loss, misuse, and
        unauthorized access, disclosure, alteration, or destruction. However, no Internet or e-mail transmission is ever
        fully secure or error free. In particular, e-mail sent to or from us may not be secure. Therefore, you should
        take special care in deciding what information you send to us via the Service or e-mail. Please keep this in
        mind when disclosing any personal information to Dawn via the Internet. In addition, we are not responsible for
        circumvention of any privacy settings or security measures contained on the Service, or third party websites.
      </p>

      <h3>8. INTERNATIONAL USERS</h3>
      <p>
        Dawn and the Service is based in the United States. By using our Service, you understand and acknowledge that
        your Personal Data will be transferred from your location to our facilities and servers in the United States,
        where data protection laws may differ from those in your jurisdiction.
      </p>

      <h3>9. YOUR CHOICES</h3>
      <p>
        In certain circumstances providing personal information is optional. However, if you choose not to provide
        personal information that is needed to use some features of our Service, you may be unable to use those
        features. You can also log in to your account or <a href="#contact-us">contact us</a> to request updates or
        corrections to your Personal Information.
      </p>

      <h3>10. CHANGES TO THE PRIVACY POLICY</h3>
      <p>
        The Service and our business may change from time to time. As a result we may change this Privacy Policy at any
        time. When we do we will post an updated version on this page, unless another type of notice is required by the
        applicable law. By continuing to use our Service or providing us with personal information after we have posted
        an updated Privacy Policy, or notified you by other means if applicable, you consent to the revised Privacy
        Policy and practices described in it.
      </p>

      <h3 id="contact-us">11. CONTACT US</h3>
      <p>
        If you have any questions about our Privacy Policy or information practices, please feel free to contact us by
        email: <a href="mailto:team@dawn.health">team@dawn.health</a>.
      </p>
    </WithLegalLayout>
  );
}
